import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // FLOLAE_GALLERY_SCROLL (2026-10-06): the grid gallery is re-rendered with the selected
        // variant's media first, so a colour change while scrolled down shows nothing new on screen.
        // Scroll the gallery back into view when its first media actually changed (colour, not size).
        const prevMediaId = flolaeFirstMediaId(this);
        const nextMediaId = flolaeFirstMediaId(newMediaGallery);

        this.replaceWith(newMediaGallery);

        if (prevMediaId !== nextMediaId) flolaeScrollGalleryIntoView(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * FLOLAE_GALLERY_SCROLL: id of the first media in the grid (falls back to the first media anywhere).
 * @param {Element} gallery
 */
function flolaeFirstMediaId(gallery) {
  const first = gallery.querySelector('.media-gallery__grid [data-media-id]') ?? gallery.querySelector('[data-media-id]');
  return first instanceof HTMLElement ? first.dataset.mediaId : undefined;
}

/**
 * FLOLAE_GALLERY_SCROLL: bring a re-rendered grid gallery back under the header on desktop.
 * Scrolls the nearest scrollable ancestor (Horizon scrolls `.page-wrapper`, not the window).
 * scrollIntoView is avoided because the gallery is `position: sticky`, which makes it unreliable.
 * No-op on mobile (carousel), for carousel presentation, or when the gallery top is already visible.
 * @param {HTMLElement} gallery
 */
function flolaeScrollGalleryIntoView(gallery) {
  if (!window.matchMedia('(min-width: 750px)').matches) return;
  if (gallery.dataset.presentation !== 'grid') return;

  const header = document.querySelector('header-component');
  const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  const delta = gallery.getBoundingClientRect().top - headerBottom;
  if (delta >= -1) return;

  let scroller = gallery.parentElement;
  while (scroller) {
    const { overflowY } = getComputedStyle(scroller);
    if ((overflowY === 'auto' || overflowY === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
    scroller = scroller.parentElement;
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduceMotion ? 'instant' : 'smooth';
  if (scroller) {
    scroller.scrollBy({ top: delta, behavior });
  } else {
    window.scrollBy({ top: delta, behavior });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
